import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Input } from '@frontend/shadcn/components/ui/input';
import { MagnifyingGlassIcon } from '@revfluence/fresh-icons/regular/esm';
import { Spinner } from '../../Analytics/components/ui/spinner';
import { SearchQueryFormData } from '../hooks/use-filters-form';
import type { CategorizedLoadingStates } from '../hooks/use-loading-states';

interface SearchFormProps {
  form: UseFormReturn<SearchQueryFormData>;
  onSubmit: (e: React.FormEvent) => void;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: (query: string) => void;
  fetching?: boolean;
  loadingStates: CategorizedLoadingStates;
}

// AI-style loading overlay for input
const AILoadingOverlay = ({ message }: { message: string }) => (
  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-start pl-4 rounded-lg">
    <div className="flex items-center space-x-2 text-blue-600">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]" />
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
      </div>
      <span className="text-sm font-medium">{message}</span>
    </div>
  </div>
);

export const SearchForm: React.FC<SearchFormProps> = ({
  form,
  onSubmit: _onSubmit,
  onSearchChange,
  onSearchSubmit,
  fetching,
  loadingStates,
}) => {
  // Extract loading states
  const { shouldShowFiltersLoading, shouldDisableInteractions } = loadingStates.uiStates;

  const getPlaceholderText = () => {
    if (shouldShowFiltersLoading) {
      return 'AI is processing your request...';
    }

    return 'Search for a creator like: Vegan beauty influencers with high engagement';
  };

  const handleSearchSubmit = (): void => {
    const prompt = form.watch('prompt');
    onSearchSubmit(prompt || '');
  };

  const isSearchDisabled = useMemo(() => {
    return (
      shouldDisableInteractions ||
      shouldShowFiltersLoading ||
      Object.keys(form.formState.errors).length > 0
    );
  }, [shouldDisableInteractions, shouldShowFiltersLoading, form.formState.errors]);

  // Handle form submission - only submit if the current search context has valid data
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only submit if we have an active search context and can search
    if (!shouldShowFiltersLoading) {
      handleSearchSubmit();
    }
  };

  return (
    <div className="flex p-3 flex-col items-start gap-2 self-stretch rounded-2xl border border-[#E5E8EB] bg-white">
      <form onSubmit={handleFormSubmit} id="search-form" className="w-full flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder={getPlaceholderText()}
            value={form.watch('prompt') || ''}
            onChange={onSearchChange}
            className={`w-full border-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0 ${
              shouldShowFiltersLoading ? 'bg-blue-50/50' : ''
            }`}
            aria-label="Search creators"
            disabled={shouldShowFiltersLoading}
          />
          {shouldShowFiltersLoading && <AILoadingOverlay message="Generating AI suggestions" />}
        </div>
        <button
          type="button"
          onClick={handleSearchSubmit}
          className={`flex w-8 h-8 px-2 py-1 justify-center items-center rounded-lg bg-[#16282D] hover:bg-[#0f1f24] disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all duration-200 ${
            shouldShowFiltersLoading ? 'animate-pulse' : ''
          }`}
          aria-label={shouldShowFiltersLoading ? 'AI is processing...' : 'Search'}
          disabled={isSearchDisabled}
        >
          {shouldShowFiltersLoading ? (
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
              <div className="w-1 h-1 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
              <div className="w-1 h-1 bg-white rounded-full animate-bounce" />
            </div>
          ) : fetching ? (
            <Spinner size="sm" spinning className="text-white" />
          ) : (
            <MagnifyingGlassIcon className="h-4 w-4 text-white" />
          )}
        </button>
      </form>
    </div>
  );
};
